import React, { useEffect, useRef, useState } from "react";

export interface EnhancedBeamContextMenuItem {
  id: string;
  label: string;
  icon?: string;
  action?: () => void;
  submenu?: EnhancedBeamContextMenuItem[];
}

interface EnhancedBeamContextMenuProps {
  x: number;
  y: number;
  visible: boolean;
  items: EnhancedBeamContextMenuItem[];
  onClose: () => void;
}

export const EnhancedBeamContextMenu: React.FC<
  EnhancedBeamContextMenuProps
> = ({ x, y, visible, items, onClose }) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Track a path of active submenu IDs
  const [activeSubmenuPath, setActiveSubmenuPath] = useState<string[]>([]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };

    const onDoc = (e: MouseEvent) => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target as Node)) onClose();
    };

    if (visible) {
      document.addEventListener("keydown", onKey);
      document.addEventListener("mousedown", onDoc);
    }

    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDoc);
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    };
  }, [visible, onClose]);

  const handleMouseLeave = () => {
    hoverTimeoutRef.current = setTimeout(() => {
      onClose();
      setActiveSubmenuPath([]);
    }, 500);
  };

  const handleMouseEnter = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
  };

  if (!visible) return null;

  // Recursive function to render menu items
  const renderMenuItems = (
    menuItems: EnhancedBeamContextMenuItem[],
    parentPath: string[] = []
  ) => {
    return menuItems.map((item) => {
      const path = [...parentPath, item.id];
      const isActive = activeSubmenuPath.join(",").startsWith(path.join(","));

      return (
        <div
          key={item.id}
          className="relative"
          onMouseEnter={() => setActiveSubmenuPath(path)}
          onMouseLeave={() =>
            setActiveSubmenuPath((prev) =>
              prev.join(",").startsWith(parentPath.join(",")) ? parentPath : []
            )
          }
        >
          <button
            onClick={() => {
              if (item.action) item.action();
              if (!item.submenu) onClose();
            }}
            className="w-full text-left px-4 py-3 hover:bg-blue-50 flex items-center gap-3 transition-colors duration-150 border-b border-gray-100 last:border-b-0 first:rounded-t-lg last:rounded-b-lg"
          >
            {item.icon && <span className="text-lg">{item.icon}</span>}
            <span className="font-medium text-gray-800">{item.label}</span>
            {item.submenu && <span className="ml-auto text-black">▶</span>}
          </button>

          {item.submenu && isActive && (
            <div
              className="absolute top-0 left-full bg-white border rounded-lg shadow-xl border-gray-200 w-48 text-sm"
              style={{ zIndex: 10000 }}
            >
              {renderMenuItems(item.submenu, path)}
            </div>
          )}
        </div>
      );
    });
  };

  return (
    <div
      ref={ref}
      style={{ left: x, top: y, zIndex: 9999 }}
      className="fixed bg-white border rounded-lg shadow-xl border-gray-200 w-48 text-sm enhanced-beam-context-menu"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {renderMenuItems(items)}
    </div>
  );
};

export default EnhancedBeamContextMenu;
